/**
 * [BaselineMain] Main container for Baseline Management module.
 * Handles workflow for:
 * - Loading baselines and user groups
 * - Creating, updating, deleting, copying, importing/exporting baselines
 * - Multi-step navigation and access control
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { useLocation, useNavigate } from "react-router-dom";

// Redux slices and actions
import { setSelectedBaseline, clearSelectedBaseline } from "src/slices/baselineSlice";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";
import { useBaselinesData } from "src/hooks/useBaselinesData";
import { useBaselineActions } from "src/hooks/useBaselineActions";
import { useDialogBox } from "src/hooks/useDialogBox";
import { useStepSaveGuard } from "src/hooks/useStepSaveGuard";

// Layout/UI
import { Box, Button, MetreaTooltip } from "@metrea-ui/v2";
import Stepper from "src/components/UI/common/Stepper";
import RecordNav from "src/components/UI/RecordNav/RecordNav";
import { PageHeader } from "src/components/SiteLayout/Header";
import { PageFooter } from "src/components/SiteLayout/Footer";
import { formFieldWidth } from "./BaselineVariables";
import BaselineList from "./BaselineList";
import BaselineBasicInfo from "./BaselineBasicInfo";
import BaselineConfigs from "./BaselineConfigs";
import BaselineItems from "./BaselineItems";
import { InputFileUpload } from "src/components/UI/Upload/InputFileUpload";
import { getBaselineImportPayloadErrorMessages } from "./baselineImportValidation";

// Utilities
import { isTestEnv } from "src/utils/appUtils";
import { baselineAccess } from "src/utils/baselineUtils";
import { Session, isDevTeam, isAdmin, isDeveloper, isOwner, isDesigner } from "src/utils/sessionUtils";
import { deepClone, getIdFromPath } from "src/utils/dataUtils";
import { debugLog, showTesting } from "src/utils/debugUtils";

// Constants
import { initialBaselineState } from "src/constants/initialStates";

const stepInstuctions = [
	"Enter the basic details for this baseline. All provided data will be applied across the entire baseline and used in all related scenarios.",
	"Configure the baseline settings. The entered values will be used for budget calculations in related scenarios, and the listed items will be available as selectable options. For instance, Military Service, Domain, Category, and Force Structure Metric will appear on the Baseline Item details page, while Operational and Geographic Preferences will be on the Scenario Strategic Preferences page.",
	"Provide detailed information for each Baseline Item. When adding a new item, you must save it first to enable certain data fields. For example, photos and target data fields can only be added or updated after the item is saved and linked to the selected Option Type.",
	"Set appropriate access permissions for this baseline by assigning it to specific groups or users.",
	"",
];
const dataChangedAlert = "Data is modified. Please save (or revert) data before continue to another step.";

// --- Main Component ---
const BaselineMain = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();

	// --- Redux Selectors ---
	// - selectedBaseline is the current baseline (or null if on the list page)
	// - showList is derived from Redux: show list only if nothing selected
	const selectedBaseline = useSelector((state) => state.baselines.selectedBaseline);
	const showList = useSelector((state) => !state.baselines.selectedBaseline);
	// Some real-mode tests preload only part of the slice state, so keep these
	// selectors tolerant of a missing `error` object while preserving normal app behavior.
	const retrieveBaselinesError = useSelector((state) => state.baselines.error?.retrieve ?? null);
	const retrieveBaselineDetailError = useSelector((state) => state.baselines.error?.retrieveOne ?? null);

	// --- Local State ---
	const initialSteps = [
		"Basic Information",
		"Baseline Configurations",
		"Baseline Items",
		"Access Management",
	];
	const [pageMode, setPageMode] = useState(props.mode || "edit");
	const [steps, setSteps] = useState(initialSteps);
	const [activeStep, setActiveStep] = useState(0);
	const [completed, setCompleted] = useState({});
	const [errorStep, setErrorStep] = useState({});
	const [disableStep, setDisableStep] = useState({});
	const [errorStepTooltip, setErrorStepTooltip] = useState({});
	const [localBaseline, setLocalBaseline] = useState(
		selectedBaseline && Object.keys(selectedBaseline || {}).length > 0
			? deepClone(selectedBaseline)
			: null,
	);
	const [dataChanged, setDataChanged] = useState(false);
	const [errorFound, setErrorFound] = useState(false);
	const [configsFieldErrorFound, setConfigsFieldErrorFound] = useState(false);
	const [resetKey, setResetKey] = useState(0);

	// Custom hooks
	const { lookups, allUserGroups } = useLookupsData();

	const { showDialog, withConfirmation, withError } = useDialogBox();

	const { fetchBaselines, fetchBaselineByIdOrUuid } = useBaselinesData();

	const {
		createBaselineAction,
		updateBaselineAction,
		deleteBaselineAction,
		copyBaselineAction,
		updateStatusAction,
		exportBaselineAction,
		dumpBaselineAction,
		importBaselineAction,
		updateThumbnailsAction,
	} = useBaselineActions({
		fetchBaselineByIdOrUuid,
		fetchBaselines,
		clearDataError,
		handleBackToList,
		localBaseline,
		activeStep,
		selectedBaseline,
		lookups,
		setLocalBaseline,
	});

	const isStep1Valid = useMemo(() => {
		if (!localBaseline) return false;

		return (
			localBaseline.name?.trim() !== "" &&
			!!localBaseline.currency_id &&
			!!localBaseline.currency_unit &&
			!errorFound
		);
	}, [localBaseline, errorFound]);

	const isStep2Valid = useMemo(() => {
		if (!localBaseline) return false;

		return (
			!configsFieldErrorFound &&
			Array.isArray(localBaseline.cfg_military_service) &&
			localBaseline.cfg_military_service.length > 0 &&
			Array.isArray(localBaseline.cfg_domain) &&
			localBaseline.cfg_domain.length > 0 &&
			Array.isArray(localBaseline.cfg_category) &&
			localBaseline.cfg_category.length > 0
		);
	}, [localBaseline, configsFieldErrorFound]);

	const stepValidators = {
		0: () => isStep1Valid,
		1: () => isStep2Valid,
		// Future:
		// 2: () => isStep3Valid,
	};

	const { activeStepIndex, canSave, hasSaveError } = useStepSaveGuard({
		activeStep,
		stepValidators,
		defaultHasError: errorFound,
	});

	// --- Derived Variables ---
	// Page Access Control
	const pageAccess = useMemo(() => {
		const data = {
			canEdit:
				pageMode === "edit" &&
				localBaseline &&
				(baselineAccess(localBaseline, allUserGroups, Session().currUser) === 2 ||
					isDeveloper() ||
					isAdmin() ||
					isOwner(localBaseline, Session().currUser.id)),
			canView: localBaseline && baselineAccess(localBaseline, allUserGroups, Session().currUser) === 1,
			canAccess: localBaseline && baselineAccess(localBaseline, allUserGroups, Session().currUser) >= 1,
		};
		return data;
	}, [localBaseline, allUserGroups, pageMode]);

	// --- Effect: deep-link AFTER router initializes URL (fix for UUID & tests) ---
	useEffect(() => {
		if (!isTestEnv && selectedBaseline) {
			dispatch(clearSelectedBaseline());
		}

		// Used for deep linking to a baseline by id/uuid in the URL
		const idFromPath = getIdFromPath(location.pathname);
		if (idFromPath) {
			fetchBaselineByIdOrUuid(idFromPath);
		} else fetchBaselines();

		debugLog(2, "[BaselineMain][FLOW] initial data load", {
			selectedBaseline: selectedBaseline,
			idFromPath: idFromPath,
			propsId: props.id,
		});
	}, [location.pathname]);

	useEffect(() => {
		if (retrieveBaselinesError) {
			// Keep Baseline list-load failures visible at the page level, matching
			// the retrieval dialog behavior already established for User/UserGroup.
			withError(retrieveBaselinesError, () => fetchBaselines(), {
				fallbackMessage: "Retrieve Baselines Error",
			});
		}
	}, [fetchBaselines, retrieveBaselinesError, withError]);

	useEffect(() => {
		if (!retrieveBaselineDetailError) return;

		const idFromPath = getIdFromPath(location.pathname);
		if (!idFromPath) return;

		const fallbackRoute = location.pathname.includes("/baseline/edit/") ? "/baseline/edit" : "/baseline";
		// Only show detail-load errors while a detail route is active; once we
		// navigate back to the list, leave the stale retrieveOne error alone.
		withError(retrieveBaselineDetailError, () => fetchBaselineByIdOrUuid(idFromPath), {
			fallbackMessage: "Retrieve Baseline Error",
			onClose: () => {
				navigate(fallbackRoute, { replace: true });
			},
		});
	}, [fetchBaselineByIdOrUuid, location.pathname, navigate, retrieveBaselineDetailError, withError]);

	useEffect(() => {
		const idFromPath = getIdFromPath(location.pathname);
		if (!idFromPath || !selectedBaseline?.id) return;
		if (!Number.isNaN(Number(idFromPath))) return;

		const normalizedPath = location.pathname.includes("/baseline/edit/")
			? `/baseline/edit/${selectedBaseline.id}`
			: `/baseline/${selectedBaseline.id}`;

		if (location.pathname !== normalizedPath) {
			// Replace UUID deep links with the standard numeric route after the detail load succeeds.
			navigate(normalizedPath, { replace: true });
		}
	}, [location.pathname, navigate, selectedBaseline?.id]);

	// --- Effect: sync local baseline on selection change ---
	useEffect(() => {
		const hasData = selectedBaseline && Object.keys(selectedBaseline || {}).length > 0;
		setLocalBaseline(hasData ? deepClone(selectedBaseline) : null);

		debugLog(3, "[BaselineMain][STATE] selectedBaseline changed", {
			selectedBaseline: selectedBaseline,
		});
	}, [selectedBaseline]);

	// --- Effect: Update step status and access as localBaseline changes ---
	useUpdateEffect(() => {
		setErrorStep({});
		setDisableStep({});
		setErrorStepTooltip({});

		// Update Step Status
		if (localBaseline) {
			if (isNew()) {
				setErrorStep({
					1: "Not available",
					2: "Not available",
					3: "Not available",
				});
				setDisableStep({
					1: "Not available",
					2: "Not available",
					3: "Not available",
				});
				setErrorStepTooltip({
					0: "Please save Step #1 before proceed to other steps.",
				});
			} else {
				if (!isReadyForItems()) {
					setErrorStep({
						1: "Need more data",
						2: "Not available",
					});
					setDisableStep({
						2: "Need more data",
					});
					setErrorStepTooltip({
						1: "Need more data in Step 2. Military Service, Domain and Category are required to proceed",
					});
				}
			}

			const nextSteps = pageAccess.canEdit ? initialSteps : initialSteps.slice(0, -1);
			setSteps(nextSteps);

			/*
				Confirmed on 2/13/2026 that 
				- BaselineMain initializes pageMode as props.mode || "edit" in BaselineMain.jsx; 
				and pageMode comparisons are only against "edit" now (BaselineMain.jsx, BaselineMain.jsx, BaselineMain.jsx, etc.)
				- there is no occurrences of:
					- pageMode === "scenario"
					- setPageMode("scenario")
					- mode="scenario"
					- across raven/raven/frontend/src
				- Baseline page renders <Baselines /> with no mode prop, so it defaults to "edit"
			*/
			// ToDo: confirm that it's safe to remove the line below - 2/13/2026
			// if (pageMode === "scenario") setSteps(initialSteps.slice(0, -1));
		}

		debugLog(3, "[BaselineMain][STATE] localBaseline/completed updated", {
			localBaseline: localBaseline,
			completed: completed,
		});
	}, [completed, localBaseline]);

	// --- Utility Functions ---
	const isNew = () => !localBaseline?.id; // Prevent running logic if localBaseline is null/undefined
	// const totalSteps = () => steps.length;
	// const completedSteps = () => Object.keys(completed).length;
	// const isLastStep = () => activeStep === totalSteps() - 1;
	// const allStepsCompleted = () => completedSteps() === totalSteps();
	// const isStepFailed = (step) => step === 1;

	const isReadyForItems = () => {
		let isReady = false;

		if (localBaseline) {
			debugLog(4, "[BaselineMain][PROCESS] isReadyForItems input", { localBaseline: localBaseline });

			if (
				// localBaseline.cfg_military_service &&
				// localBaseline.cfg_military_service.length > 0 &&
				// localBaseline.cfg_domain &&
				// localBaseline.cfg_domain.length > 0 &&
				// localBaseline.cfg_category &&
				// localBaseline.cfg_category.length > 0
				Array.isArray(localBaseline.cfg_military_service) &&
				localBaseline.cfg_military_service.length > 0 &&
				Array.isArray(localBaseline.cfg_domain) &&
				localBaseline.cfg_domain.length > 0 &&
				Array.isArray(localBaseline.cfg_category) &&
				localBaseline.cfg_category.length > 0
			)
				isReady = true;
		}

		return isReady;
	};

	// --- Derived: Step 0 (Basic Info) validity ---
	// NOTE: This aggregates child validation + required selections
	// const isStep0Valid = useMemo(() => {
	// 	if (!localBaseline) return false;

	// 	return (
	// 		localBaseline.name?.trim() !== "" &&
	// 		localBaseline.currency_id &&
	// 		localBaseline.currency_id !== 0 &&
	// 		localBaseline.currency_unit &&
	// 		localBaseline.currency_unit !== "" &&
	// 		!errorFound
	// 	);
	// }, [localBaseline, errorFound]);

	// const activeStepIndex = typeof activeStep === "number" ? activeStep : (activeStep?.index ?? 0);

	// const canSave = useMemo(() => {
	// 	if (activeStepIndex === 0) return isStep0Valid;
	// 	return !errorFound;
	// }, [activeStepIndex, isStep0Valid, errorFound]);
	// const hasSaveError = !canSave;

	function clearDataError() {
		debugLog(3, "[BaselineMain][STATE] clearDataError", {});
		setDataChanged(false);
		setConfigsFieldErrorFound(false);
		setErrorStepTooltip({});
		setErrorStep({});
		setDisableStep({});
	}

	// const refreshData = () => {
	// 	debugLog(3, "[BaselineMain] refreshData called");
	// 	dispatch(clearSelectedBaseline());
	// };

	// --- Handlers ---
	function handleBackToList() {
		debugLog(3, "[BaselineMain][ACTION] handleBackToList", {});

		dispatch(clearSelectedBaseline());
		setLocalBaseline(null); // reset local state

		debugLog(2, "[BaselineMain][FLOW] fetchBaselines started", {});
		fetchBaselines();

		handleReset();
		clearDataError();
	}

	// const handleNext = () => {
	// 	// Need to verify if this works as expected.  -ylee
	// 	if (isNew()) handleComplete();

	// 	// Find next step needs to be completed
	// 	const newActiveStep = !allStepsCompleted() ? steps.findIndex((step, i) => !(i in completed)) : 0;

	// 	debugLog(4, "[BaselineMain] handleNext", {
	// 		completed: completed,
	// 		newActiveStep: newActiveStep,
	// 		isLastStep: isLastStep(),
	// 		allStepsCompleted: allStepsCompleted(),
	// 	});

	// 	setActiveStep(newActiveStep);
	// };

	// const handleBack = () => {
	// 	debugLog(3, "[BaselineMain] handleBack called", { prevActiveStep: activeStep });
	// 	setActiveStep((prevActiveStep) => prevActiveStep - 1);
	// };

	const handleReset = () => {
		debugLog(3, "[BaselineMain][ACTION] handleReset", {});
		setActiveStep(0);
		setCompleted({});
	};

	// const handleStep = (step) => () => {
	// 	debugLog(3, "[BaselineMain] handleStep", {
	// 		step: step,
	// 		activeStep: activeStep,
	// 		dataChanged: dataChanged,
	// 	});
	// 	setActiveStep(step);
	// };

	// Verify/save data here; Save Button action
	const handleComplete = () => {
		/**
		 * Validation logic using save guard
		 * - relies on canSave and hasSaveError from the guard to determine if data is valid for saving and to show error state in the UI. -ylee
		 *
		 * Note: canSave and hasSaveError are derived from the useStepSaveGuard hook,
		 * which centralizes the validation logic based on the active step and provided validators.
		 * This allows for more scalable and maintainable validation as the component grows in complexity. -ylee
		 *
		 * Don't really need it if RecordNav buttons are disabled when data is invalid,
		 * but keeping it here for extra safety and to show error dialog if somehow triggered with invalid data. -ylee
		 */
		if (!canSave) {
			showDialog({
				title: "Error",
				message: ["Data is incomplete.", " ", "Please make verify/validate all data to continue."],
				type: "error",
			});
			debugLog(3, "[BaselineMain][ACTION] handleComplete.dataIncomplete", { activeStep: activeStep });
			return;
		}

		// Here is where to disable the "Completion Check" ylee 8/3/2023
		setCompleted((prevCompleted) => ({
			...prevCompleted,
			[activeStep]: true,
		}));

		debugLog(3, "[BaselineMain][ACTION] handleComplete", {
			activeStep: activeStep,
			newCompleted: {
				...completed,
				[activeStep]: true,
			},
			isNew: isNew(),
		});

		if (activeStep === 0 || activeStep === 1 || activeStep === 2 || activeStep === 3) {
			if (isNew()) {
				handleCreateBaseline();
			} else {
				handleSaveBaseline(activeStep);
			}
		}
	};

	const handleClearComplete = () => {
		setCompleted((prevCompleted) => {
			const nextCompleted = { ...prevCompleted };
			delete nextCompleted[activeStep];
			return nextCompleted;
		});

		debugLog(3, "[BaselineMain][ACTION] handleClearComplete", {
			newCompleted: (() => {
				const nextCompleted = { ...completed };
				delete nextCompleted[activeStep];
				return nextCompleted;
			})(),
			activeStep: activeStep,
		});
	};

	const handleLocalBaselineChange = (updateData) => {
		setLocalBaseline((prev) => ({
			...prev,
			...updateData,
		}));

		setDataChanged(true);

		debugLog(3, "[BaselineMain][ACTION] handleLocalBaselineChange", {
			updateData: updateData,
			activeStep: activeStep,
		});
	};

	const handleValidateData = (flag) => {
		setErrorFound(flag);
		debugLog(3, "[BaselineMain][ACTION] handleValidateData", { errorFound: flag });
	};

	const handleValidateConfigFieldData = (flag) => {
		if (configsFieldErrorFound === flag) return;
		setConfigsFieldErrorFound(flag);
		// High-frequency validation callback: log transitions only at higher debug level.
		debugLog(7, "[BaselineMain][ACTION] handleValidateConfigFieldData", {
			from: configsFieldErrorFound,
			to: flag,
		});
	};

	const handleStepChange = (nextStep) => {
		debugLog(3, "[BaselineMain][ACTION] handleStepChange", { nextStep: nextStep });
		if (typeof nextStep === "number" && Number.isFinite(nextStep)) {
			setActiveStep(nextStep);
			return;
		}
		if (typeof nextStep === "string" && nextStep.trim() !== "" && !Number.isNaN(Number(nextStep))) {
			setActiveStep(Number(nextStep));
			return;
		}
		if (nextStep && typeof nextStep === "object") {
			const candidate = nextStep.index ?? nextStep.step;
			if (typeof candidate === "number" && Number.isFinite(candidate)) {
				setActiveStep(candidate);
				return;
			}
			if (
				typeof candidate === "string" &&
				candidate.trim() !== "" &&
				!Number.isNaN(Number(candidate))
			) {
				setActiveStep(Number(candidate));
			}
		}
	};

	const handleCompletedChange = (nextCompleted) => {
		debugLog(3, "[BaselineMain][ACTION] handleCompletedChange", { nextCompleted: nextCompleted });
		setCompleted(nextCompleted);
	};

	const handleSaveBaseline = async (step = 999, callback = null) => {
		// Add validation if needed, or call action directly
		debugLog(2, "[BaselineMain][FLOW] updateBaselineAction started", { step: step });
		updateBaselineAction(step);
	};

	const handleCreateBaseline = () => {
		debugLog(3, "[BaselineMain][ACTION] handleCreateBaseline", { baselineId: localBaseline?.id ?? null });
		if (localBaseline.number_of_period === "") {
			showDialog({
				title: "Error",
				message: ["Invalid input", " ", "Please enter a valid number of periods."],
				type: "error",
			});
			return;
		}

		debugLog(2, "[BaselineMain][FLOW] createBaselineAction started", {});
		createBaselineAction();
	};

	const handleUpdateStatus = (newStatus) => {
		// event.preventDefault();

		let arrayMsg = [
			`You are about to archive this Baseline, ${localBaseline.name}.`,
			"This baseline would be removed from for the general users.",
			" ",
			"Do you want to proceed?",
		];
		if (newStatus === -1) {
			arrayMsg = [
				`You are about to permanently DELETE this Baseline, ${localBaseline.name}.`,
				"The data will be removed from the application.",
				"This action CANNOT be undone.",
				" ",
				"Do you want to proceed?",
			];
		}

		if (newStatus !== 1) {
			withConfirmation(
				{
					title: "Warning",
					message: arrayMsg,
				},
				() => updateStatusAction(localBaseline.id, newStatus),
			);
		} else {
			updateStatusAction(localBaseline.id, newStatus);
		}

		debugLog(3, "[BaselineMain][ACTION] handleUpdateStatus", { newStatus: newStatus });
	};

	const handleChangeStatusFromList = async (id, newStatus) => {
		updateStatusAction(id, newStatus, false); // false: don't show confirmation MsgBox

		// handleBackToList();  // already called in updateStatusAction()
		debugLog(3, "[BaselineMain][ACTION] handleChangeStatusFromList", {
			baseline_id: id,
			newStatus: newStatus,
		});
	};

	const handleCopyBaseline = () => {
		withConfirmation(
			{
				title: "Warning",
				message: [
					`You are about to make a copy of this baseline, ${localBaseline.name}.`,
					" ",
					"Do you want to proceed?",
				],
			},
			() => copyBaselineAction(localBaseline.id),
		);

		debugLog(3, "[BaselineMain][ACTION] handleCopyBaseline", {});
	};

	const handleDeleteBaseline = () => {
		withConfirmation(
			{
				title: "Warning",
				message: [
					`You are about to permanently delete/purge this baseline, ${localBaseline.name}.`,
					"The data will be removed from the database.",
					"This action CANNOT be undone.",
					" ",
					"Do you want to proceed?",
				],
			},
			() => deleteBaselineAction(localBaseline.id),
		);

		debugLog(3, "[BaselineMain][ACTION] handleDeleteBaseline", {});
	};

	const handleExportBaseline = async () => {
		withConfirmation(
			{
				title: "Confirm",
				message: [
					`You are about to export this baseline, ${localBaseline?.name}, in the JSON format..`,
					" ",
					"Do you want to proceed?",
				],
				checkboxLabel: showTesting() ? "Include photos (images & thumbnails)" : null,
				checkboxDefault: false,
			},
			(checked) => exportBaselineAction({ includePhotos: !!checked }),
		);

		debugLog(3, "[BaselineMain][ACTION] handleExportBaseline", {});
	};

	const handleDumpBaseline = async () => {
		withConfirmation(
			{
				title: "Confirm",
				message: [
					`You are about to dump data of this baseline, ${localBaseline.name}, in the XLSX format.`,
					" ",
					"Do you want to proceed?",
				],
			},
			() => dumpBaselineAction(),
		);

		debugLog(3, "[BaselineMain][ACTION] handleDumpBaseline", {});
	};

	const handleImportBaseline = (data) => {
		// Validate upload payload before confirmation so malformed marker payloads
		// (parseError/invalidType/unexpected shape) never enter the confirm/mutation path.
		const importPayloadErrorMessages = getBaselineImportPayloadErrorMessages(data);
		if (importPayloadErrorMessages) {
			showDialog({
				title: "Error",
				message: importPayloadErrorMessages,
				type: "error",
			});
			return;
		}

		withConfirmation(
			{
				title: "Confirm",
				message: [
					`You are about to import this baseline, ${data.name}.`,
					" ",
					"Do you want to proceed?",
				],
			},
			() => {
				const now = new Date();
				const importData = {
					...data,
					name: `${data.name} (imported: ${now.toLocaleDateString("en-US")})`,
					description:
						`${data.description ?? ""}\n\nImport file: ${data.filename}` +
						`\nImport on: ${data.importDt}`,
					from_id: -data.id,
					id: null,
					createdBy: Session().currUser.id,
					createdAt: now,
					updatedBy: Session().currUser.id,
					updatedAt: now,
				};

				importBaselineAction(importData);
			},
		);

		debugLog(3, "[BaselineMain][ACTION] handleImportBaseline", { data: data });
	};

	const handleUpdateThumbnails = async () => {
		withConfirmation(
			{
				title: "Confirm",
				message: [
					`You are about to create/update thumbnails for the baseline items.`,
					" ",
					"Do you want to proceed?",
				],
			},
			() => updateThumbnailsAction(),
		);

		debugLog(3, "[BaselineMain][ACTION] handleUpdateThumbnails", {});
	};

	const handleRevertData = () => {
		debugLog(3, "[BaselineMain][ACTION] handleRevertData", { localBaseline: localBaseline });
		setResetKey((prev) => prev + 1);
		debugLog(2, "[BaselineMain][FLOW] fetchBaselineByIdOrUuid started", { id: localBaseline?.id });
		fetchBaselineByIdOrUuid(localBaseline.id);

		setCompleted({});
		clearDataError();
	};

	const clickNewHandler = (event) => {
		debugLog(3, "[BaselineMain][ACTION] clickNewHandler", {});
		dispatch(setSelectedBaseline(initialBaselineState(lookups)));
	};

	const clickBackToListHandler = (option) => {
		debugLog(3, "[BaselineMain][ACTION] clickBackToListHandler", { option: option });
		handleBackToList();
	};

	const clickSaveHandler = (option) => {
		debugLog(3, "[BaselineMain][ACTION] clickSaveHandler", { option: option });
		handleComplete();
	};

	const clickArchiveHandler = (option, newStatus) => {
		debugLog(3, "[BaselineMain][ACTION] clickArchiveHandler", { option: option, newStatus: newStatus });
		handleUpdateStatus(newStatus);
	};

	const clickRevertHandler = (option) => {
		debugLog(3, "[BaselineMain][ACTION] clickRevertHandler", { option: option });
		handleRevertData();
	};

	const clickPurgeHandler = (option) => {
		debugLog(3, "[BaselineMain][ACTION] clickPurgeHandler", { option: option });
		handleDeleteBaseline();
	};

	const clickDeleteHandler = (option) => {
		debugLog(3, "[BaselineMain][ACTION] clickDeleteHandler", { option: option });
		handleUpdateStatus(-1);
	};

	const clickDataDumpHandler = (option) => {
		debugLog(3, "[BaselineMain][ACTION] clickDataDumpHandler", { option: option });
		handleDumpBaseline();
	};

	const clickExportHandler = (option) => {
		debugLog(3, "[BaselineMain][ACTION] clickExportHandler", { option: option });
		handleExportBaseline();
	};

	const clickCopyHandler = (option) => {
		debugLog(3, "[BaselineMain][ACTION] clickCopyHandler", { option: option });
		handleCopyBaseline();
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[BaselineMain][FLOW] render", {
		renderCount: renderCount.current,
		selectedBaseline: selectedBaseline,
	});
	debugLog(9, "[BaselineMain][FLOW] renderState", {
		selectedBaseline: selectedBaseline,
		localBaseline: localBaseline,
		dataChanged: dataChanged,
		errorStepTooltip: errorStepTooltip,
		errorStep: errorStep,
		disableStep: disableStep,
		currUser: Session().currUser,
		lookups: lookups,
		errorFound: errorFound,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	// ✅ TEMP test debug
	// console.log("[TEST DEBUG] localBaseline =", !!localBaseline);
	// console.log("[TEST DEBUG] isAdmin() inside component =", isAdmin?.());

	return (
		<div data-testid="BaselineMain">
			{/* Page Header (with import/add new as allowed) */}
			<PageHeader pageTitle="Baselines" actions={true}>
				{!localBaseline && pageMode === "edit" && (isAdmin() || isDeveloper()) && (
					<InputFileUpload
						label="Import"
						color="secondary"
						toolTip="Import Baseline (from a previously exported JSON file)"
						onLoad={handleImportBaseline}
					/>
				)}
				{!localBaseline && (isAdmin() || isDesigner() || isDeveloper()) && (
					<MetreaTooltip content="Add New Baseline">
						<Button onClick={clickNewHandler} style={{ padding: "10px" }}>
							Add New
						</Button>
					</MetreaTooltip>
				)}
			</PageHeader>

			<Box className="pageContent" pt={0}>
				{/* List view */}
				{!localBaseline && (
					<div className="col-md-12">
						{showList && (
							<>
								<BaselineList
									mode={pageMode}
									formFieldWidth={formFieldWidth}
									onSelectBaseline={fetchBaselineByIdOrUuid}
									onChangeStatus={handleChangeStatusFromList}
								/>
							</>
						)}
					</div>
				)}

				{/* Detail view: Stepper and forms */}
				{localBaseline && (
					<>
						<Box w="100%">
							<RecordNav
								callFrom="Baseline"
								showBack
								{...(pageMode === "edit"
									? {
											...(showTesting() && isDevTeam() ? { showPurge: true } : {}),
											...(isAdmin()
												? {
														showDelete: true,
														showDataDump: true,
														showExport: true,
													}
												: {}),
											...(pageAccess.canEdit
												? {
														showCopy: true,
														...(localBaseline.status_id !== 1
															? { showRestore: true }
															: { showArchive: true }),
														showRevert: true,
														showSave: true,
													}
												: {}),
										}
									: {})}
								onClickBackToList={clickBackToListHandler}
								onClickPurge={clickPurgeHandler}
								onClickDelete={clickDeleteHandler}
								onClickDataDump={clickDataDumpHandler}
								onClickExport={clickExportHandler}
								onClickCopy={clickCopyHandler}
								onClickArchive={clickArchiveHandler}
								onClickRevert={clickRevertHandler}
								onClickSave={clickSaveHandler}
								hasError={hasSaveError}
								disabled={dataChanged && activeStepIndex === 2}
							/>

							{/* ToDo: Need to update Handlers. ex. setActiveStep, setCompleted */}
							<Stepper
								title="Baseline Steps"
								steps={steps}
								// control the active step
								value={activeStep}
								onChange={handleStepChange}
								// completion map
								completed={completed}
								onCompletedChange={handleCompletedChange}
								disabledSteps={disableStep} // map or array is fine
								errorStepTooltip={errorStepTooltip} // small helper icon with tooltip
								errorStep={errorStep} // fallback messages (optional, but supported)
								dataChanged={dataChanged && !isNew()} // red “changed” icon on the active step
								dataChangedTooltip={dataChangedAlert}
								// blockNavigationOnChange // when dataChanged, other steps disabled
								isNew={isNew()} // only shows helper icon during “new”
								// Save logic; return false to block completion
								onSave={(idx) => {
									let ok = true;
									if (idx === 0 && localBaseline.name.trim() === "") ok = false;
									return ok;
								}}
							/>
							<div>
								<React.Fragment>
									<Box mt="20px">
										{pageMode === "edit" && (
											<Box className="mx-2 mb-8 text-xs">
												{stepInstuctions[activeStep]}
											</Box>
										)}

										{activeStep === 0 && (
											<Box data-testid="tab-basic-information">
												<BaselineBasicInfo
													formFieldWidth={formFieldWidth}
													item={localBaseline}
													mode={pageAccess?.canEdit ? pageMode : "view"}
													onLocalBaselineChange={handleLocalBaselineChange}
													onValidateData={handleValidateData}
													onClearComplete={handleClearComplete}
													onSetDataChanged={setDataChanged}
												/>
											</Box>
										)}
										{activeStep === 1 && (
											<Box data-testid="tab-baseline-configurations">
												<BaselineConfigs
													formFieldWidth={formFieldWidth}
													item={localBaseline}
													mode={pageAccess?.canEdit ? pageMode : "view"}
													dataChanged={dataChanged}
													groupId={100}
													onSaveDataToDB={handleSaveBaseline}
													onLocalBaselineChange={handleLocalBaselineChange}
													onValidateData={handleValidateConfigFieldData}
													onClearComplete={handleClearComplete}
													onSetDataChanged={setDataChanged}
													resetKey={resetKey}
												/>
											</Box>
										)}
										{activeStep === 2 && (
											<Box data-testid="tab-baseline-items">
												{isDevTeam() && showTesting() && (
													<>
														<Button
															onClick={handleUpdateThumbnails}
															style={{ padding: "10px", marginBottom: "10px" }}
														>
															Update Thumbnails
														</Button>
													</>
												)}
												<BaselineItems
													formFieldWidth={formFieldWidth}
													item={localBaseline}
													mode={pageAccess?.canEdit ? pageMode : "view"}
													onLocalBaselineChange={handleLocalBaselineChange}
													onSaveDataToDB={handleSaveBaseline}
													onClearComplete={handleClearComplete}
													onReloadBaseline={fetchBaselineByIdOrUuid}
													onSetDataChanged={setDataChanged}
												/>
											</Box>
										)}
										{activeStep === 3 &&
											// Access Management can only be changed by Admin, Developer and Origianl Creater
											pageAccess.canAccess && (
												<Box data-testid="tab-access-management">
													<BaselineConfigs
														formFieldWidth={formFieldWidth}
														item={localBaseline}
														mode={pageAccess?.canEdit ? pageMode : "view"}
														groupId={200}
														onLocalBaselineChange={handleLocalBaselineChange} // Updates parent state
														onClearComplete={handleClearComplete} // Triggers save to DB
													/>
												</Box>
											)}
									</Box>
								</React.Fragment>
							</div>
						</Box>
					</>
				)}
				<PageFooter />
			</Box>
		</div>
	);
};

export default BaselineMain;
